/* eslint react/no-multi-comp: 0 */
import React from 'react';
import { theme, type SpinProps as AntdSpinProps } from 'antd';

// Build on the native Spin primitive (which owns the AntdSpin escape hatch);
// LoadingDots is a Spin with a custom dots indicator.
import Spin from '../Spin';

export type LoadingDotsSize = 'sm' | 'md' | 'lg';
export type LoadingDotsContext = 'default' | 'white';

export type LoadingDotsProps = Omit<
    AntdSpinProps,
    'indicator' | 'size' | 'tip' | 'percent' | 'children'
> & {
    /** Dot scale. Defaults to `'md'` (matches the legacy default dots). */
    size?: LoadingDotsSize;
    /**
     * Color context. `'default'` pulses the brand primary token; `'white'`
     * pulses white for use on dark/colored backgrounds.
     */
    context?: LoadingDotsContext;
    /** Accessible label announced by assistive tech. Defaults to `'Loading'`. */
    label?: string;
};

// Owned dimension scale for the dots indicator. AntD has no loading-dots token,
// so these are named owned constants rather than scattered magic numbers. They
// reproduce the legacy `@relo` LoadingDots scale (`sm`/default/`lg`) one to one.
const sizeScale: Record<
    LoadingDotsSize,
    { dot: number; gap: number; height: number }
> = {
    // dot = diameter (rem), gap = center-to-center spacing (rem),
    // height = container height (rem) reserved for the row.
    sm: { dot: 0.25, gap: 0.5, height: 0.5 },
    md: { dot: 0.5, gap: 0.8125, height: 0.75 },
    lg: { dot: 1, gap: 1.625, height: 1.25 },
};

// Map the owned dots scale to AntD Spin's own size vocabulary so the Spin
// wrapper class stays semantically correct (the dots themselves are sized by
// the indicator below).
const spinSizeFor: Record<LoadingDotsSize, AntdSpinProps['size']> = {
    sm: 'small',
    md: 'default',
    lg: 'large',
};

// Staggered pulse timing ported from the legacy dots (left 0s, center 0.25s,
// right 0.5s) so the wave direction and cadence match.
const dotDelays = ['0s', '0.25s', '0.5s'];

const ANIMATION_NAME = 'app-ui-loading-dots-pulse';
const ANIMATION_NAME_WHITE = 'app-ui-loading-dots-pulse-white';
const STYLE_ELEMENT_ID = 'app-ui-loading-dots-keyframes';

// Inject the pulse keyframes once. The dots indicator styles on top of AntD
// Spin and does not depend on any global stylesheet, so the animation travels
// with the component (extraction-clean).
const ensureKeyframes = (primaryColor: string) => {
    if (typeof document === 'undefined') {
        return;
    }

    const existing = document.getElementById(STYLE_ELEMENT_ID);
    const css = `
@keyframes ${ANIMATION_NAME} {
    0% { background: ${primaryColor}26; }
    70% { background: ${primaryColor}; }
    100% { background: ${primaryColor}26; }
}
@keyframes ${ANIMATION_NAME_WHITE} {
    0% { background: rgba(255, 255, 255, 0.15); }
    70% { background: rgba(255, 255, 255, 1); }
    100% { background: rgba(255, 255, 255, 0.15); }
}`;

    if (existing) {
        if (existing.textContent !== css) {
            existing.textContent = css;
        }
        return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = css;
    document.head.appendChild(style);
};

// The dots themselves, rendered as AntD Spin's `indicator`. AntD clones the
// indicator and merges its own `ant-spin-dot` className plus a `percent` prop,
// so we accept and spread `className` and drop `percent`.
type DotsIndicatorProps = {
    primaryColor: string;
    size: LoadingDotsSize;
    context: LoadingDotsContext;
    label: string;
    className?: string;
    // AntD Spin's Indicator injects `percent`; it is not relevant to the dots.
    percent?: number;
};

const DotsIndicator: React.FC<DotsIndicatorProps> = ({
    primaryColor,
    size,
    context,
    label,
    className,
    percent: _percent,
    ...rest
}) => {
    const { dot, gap, height } = sizeScale[size];
    const animationName =
        context === 'white' ? ANIMATION_NAME_WHITE : ANIMATION_NAME;

    return (
        <span
            aria-label={label}
            className={className}
            role="status"
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                // Neutralize AntD `.ant-spin-dot` sizing (width/height: 1em,
                // font-size: dotSize) so the dots row sizes to its own content.
                fontSize: 0,
                gap: `${gap - dot}rem`,
                height: `${height}rem`,
                justifyContent: 'center',
                width: 'auto',
            }}
            {...rest}
        >
            {dotDelays.map(delay => (
                <span
                    aria-hidden="true"
                    data-testid="loading-dot"
                    key={delay}
                    style={{
                        animation: `${animationName} 3s infinite`,
                        animationDelay: delay,
                        background: `${primaryColor}26`,
                        borderRadius: '50%',
                        height: `${dot}rem`,
                        width: `${dot}rem`,
                    }}
                />
            ))}
        </span>
    );
};

type LoadingDotsComponent = React.ForwardRefExoticComponent<
    LoadingDotsProps & React.RefAttributes<HTMLDivElement>
>;

/**
 * Indeterminate dots loading indicator. This IS an AntD `Spin`: the dots are
 * supplied as Spin's custom `indicator`, and Spin provides the loading wrapper
 * and a11y (`aria-live`/`aria-busy`). The dots look (count, size, brand color,
 * staggered pulse) is styled on top with the owned tokens.
 */
const LoadingDots = React.forwardRef<HTMLDivElement, LoadingDotsProps>(
    (
        {
            size = 'md',
            context = 'default',
            label = 'Loading',
            spinning = true,
            ...props
        },
        // AntD `Spin` is a plain function component and does not accept a ref;
        // the forwardRef shell preserves the house signature without forwarding.
        _ref,
    ) => {
        const { token } = theme.useToken();
        // Color flows from the owned brand tokens via <UiProvider>; nothing is
        // hardcoded. `white` context pulses white for dark backgrounds.
        const primaryColor = token.colorPrimary;

        // Inject keyframes synchronously during render. `ensureKeyframes` is
        // idempotent and DOM-guarded, so this is safe and avoids a post-render
        // effect (which would otherwise need to run inside React's commit phase).
        ensureKeyframes(primaryColor);

        return (
            <Spin
                indicator={
                    <DotsIndicator
                        context={context}
                        label={label}
                        primaryColor={primaryColor}
                        size={size}
                    />
                }
                size={spinSizeFor[size]}
                spinning={spinning}
                {...props}
            />
        );
    },
) as LoadingDotsComponent;

LoadingDots.displayName = 'LoadingDots';

export default LoadingDots;
